import * as DialogPrimitive from '@radix-ui/react-dialog';

import { Column } from '../Containers';
import { XIcon } from '../Icons';

import { dynamicStyling } from 'styles';

import { OverlayHeader } from './Header';
import { OverlayBackground } from './Background';
import { OverlayCommonProps } from './types';
import { OVERLAY_BG_IDS } from './escape';

import { useToggleV2, useWatch } from 'hooks';
import { safeStateUpdate } from 'utils/react';

export type ModalProps = OverlayCommonProps & {
  maxWidth?: string;
  width?: string;

  placement?: 'center' | 'top';
};

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const ModalTrigger = DialogPrimitive.Trigger;

type ModalContentProps = Pick<
  ModalProps,
  | 'className'
  | 'children'
  | 'close'
  | 'maxWidth'
  | 'width'
  | 'showCloseButton'
  | 'preventOpenAutoFocus'
  | 'id'
  | 'placement'
>;

const alignmentClasses = {
  center: 'items-center',
  top: 'items-start mt-8',
};

function DialogContent({
  className,
  children,
  close,
  maxWidth,
  width,
  showCloseButton = true,
  preventOpenAutoFocus = false,
  id,
  placement = 'center',
  ...props
}: ModalContentProps) {
  return (
    <DialogPortal>
      <OverlayBackground id={id ?? OVERLAY_BG_IDS.modal} />

      {/* If we use transform + fixed like shadcn, we create a containing block... every fixed element inside of it would be relative to <Content />, not the view port */}
      <div className={`fixed inset-0 z-50 flex justify-center ${alignmentClasses[placement]}`}>
        <DialogPrimitive.Content
          {...props}
          // do not focus the trigger back when closing modal
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={preventOpenAutoFocus ? (e) => e.preventDefault() : undefined}
          {...dynamicStyling({
            maxWidth,
            width,

            className: [
              'scrollbar relative z-50 grid max-h-[90vh] min-w-64 max-w-2xl gap-4 phone:max-h-[85vh] phone:max-w-[90vw]',
              'rounded-lg border bg-background p-6 pt-4 text-foreground shadow-lg phone:p-4',
              'duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-[2%]',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-top-[2%]',
              className,
            ],
          })}
        >
          {children}

          {showCloseButton && (
            <DialogClose
              onClick={close}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            >
              <XIcon size={16} />
              <span className="sr-only">Close</span>
            </DialogClose>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
}

export function Modal({
  gap,
  title,
  description,
  children,
  isOpen: controlledIsOpen,
  close,
  trigger,
  allowEscape = true,
  ...props
}: ModalProps) {
  const [isOpened, { setIsOn, turnOff, turnOn }] = useToggleV2(controlledIsOpen);

  useWatch(controlledIsOpen, (newOpen) => (newOpen ? turnOn() : turnOff()));

  const actualClose = () => {
    setIsOn(false);

    if (close) safeStateUpdate(close);
  };

  const handleOpenChange = (newState: boolean): void => {
    if (newState) return setIsOn(newState);

    if (!allowEscape) return;

    actualClose();
  };

  return (
    <Dialog open={isOpened} onOpenChange={handleOpenChange}>
      <DialogContent close={actualClose} {...props}>
        <Column gap={gap}>
          <OverlayHeader title={title} description={description} />

          {children}
        </Column>
      </DialogContent>

      {trigger && <ModalTrigger asChild>{trigger}</ModalTrigger>}
    </Dialog>
  );
}
